import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Pencil, Trash2, Plus, RotateCcw, ShieldAlert, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-08 — stage 3 of the meeting page: the debrief, made from what was logged at stage 2.
 *
 * Every item shows the quote it rests on and where in the call it was said. The person can reword,
 * delete or add items and change the topic tags; those changes are kept and win over any re-run.
 * Items from a passage flagged as sensitive are marked, because they are left out of drafts and CRM notes.
 */

interface Item {
  id: string;
  text: string;
  quote?: string | null;
  ref?: string | null;
  edited?: boolean;
  added?: boolean;
  restricted?: boolean;
  original_text?: string;
  type?: string;
  owner?: 'us' | 'them';
  owner_name?: string | null;
  due_date?: string | null;
  date?: string | null;
  kind?: string | null;
  title?: string | null;
  org?: string | null;
  name?: string;
}
interface Proposal {
  ok?: boolean; skipped?: string; error?: string; count?: number; questions?: string[];
  summary?: { notes?: number; notes_added?: number; tasks?: number; reminders?: number }; // BD-11 activity record
}
interface Debrief {
  status: 'none' | 'running' | 'ready' | 'failed';
  captures?: number;
  source?: string | null;
  error?: string | null;
  generated_at?: string | null;
  summary?: Item[];
  decisions?: Item[];
  commitments?: Item[];
  needs?: Item[];
  priorities?: Item[];
  risks?: Item[];
  stakeholders?: Item[];
  buying_signals?: Item[];
  open_questions?: Item[];
  topic_tags?: string[];
  deleted_items?: { section: string; id: string; text: string }[];
  sensitivity_flags?: { type: string; ref: string }[];
  proposals?: { zoho?: Proposal; follow_up?: Proposal; activity?: Proposal } | null;
}

type Section = 'summary' | 'decisions' | 'commitments' | 'needs' | 'priorities' | 'risks' | 'stakeholders' | 'buying_signals' | 'open_questions';

const SECTIONS: { key: Section; label: string; hint?: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'decisions', label: 'Decisions' },
  { key: 'commitments', label: 'Commitments' },
  { key: 'needs', label: 'Needs and pain points', hint: 'In the client\'s own words.' },
  { key: 'priorities', label: 'Priorities and initiatives' },
  { key: 'risks', label: 'Risks and objections' },
  { key: 'buying_signals', label: 'Buying signals', hint: 'Only what was actually said about budget, timing, authority or process.' },
  { key: 'stakeholders', label: 'New people mentioned', hint: 'Not in Zoho. Each becomes a proposed contact in Approvals; nothing is written to Zoho until you approve it.' },
  { key: 'open_questions', label: 'Open questions' },
];

const TOPIC_TAGS = [
  'ai_strategy', 'generative_ai', 'ai_governance', 'model_risk', 'data_platform', 'data_quality', 'analytics_reporting',
  'process_automation', 'operating_model', 'org_change', 'talent_skills', 'risk_controls', 'regulatory_compliance',
  'cybersecurity', 'cloud_migration', 'vendor_selection', 'system_integration', 'cost_reduction', 'revenue_growth', 'customer_experience',
];

const SOURCE_LABEL: Record<string, string> = { transcript: 'From a transcript', notes: 'From your notes', voice: 'From a voice debrief' };
const SKIP_REASON: Record<string, string> = {
  step_off: 'switched off for you',
  not_bd: 'not a BD meeting',
  not_external: 'not an external meeting',
  delivery_meeting: 'a delivery meeting',
  meeting_cancelled: 'the meeting was cancelled',
  no_external_recipient: 'nobody outside to follow up with',
  outlook_not_connected: 'your Outlook is not connected yet',
  zoho_not_ready: 'Zoho is not connected yet',
  assistant_off: 'your BD Assistant is off',
  personal_meeting: 'a personal meeting; personal items never go to Jarvis PM',
  no_known_contact: 'nobody on the meeting is a known contact yet',
  pm_project_archived: 'the Business Development project is archived',
};

/** BD-11: what the meeting added to Jarvis PM (contact notes and the owner's tasks). */
const activityNote = (p?: Proposal): string | null => {
  if (!p) return null;
  if (p.error) return 'Jarvis PM: could not be recorded.';
  if (p.skipped) return `Jarvis PM: nothing recorded (${SKIP_REASON[p.skipped] || p.skipped.replace(/_/g, ' ')}).`;
  const s = p.summary || {};
  const parts: string[] = [];
  const added = s.notes_added || 0;
  const waiting = (s.notes || 0) - added;
  if (added) parts.push(`meeting note added to ${added} contact task${added === 1 ? '' : 's'}`);
  if (waiting > 0) parts.push(`${waiting} meeting note${waiting === 1 ? '' : 's'} to approve`);
  if (s.tasks) parts.push(`${s.tasks} task${s.tasks === 1 ? '' : 's'} from your commitments to approve`);
  if (s.reminders) parts.push(`${s.reminders} follow-up reminder${s.reminders === 1 ? '' : 's'} set (they committed to something)`);
  return `Jarvis PM: ${parts.length ? parts.join('; ') : 'nothing new'}.${waiting > 0 || s.tasks ? ' Review in Approvals.' : ''}`;
};
const SIGNAL_LABEL: Record<string, string> = { budget: 'Budget', timing: 'Timing', authority: 'Authority', process: 'Process' };
const KIND_LABEL: Record<string, string> = {
  verbal_award: 'Verbal award', formal_approval: 'Formal approval', sow_created: 'SOW created', sow_sent: 'SOW sent', sow_signed: 'SOW signed',
};

const tagLabel = (t: string): string => t.replace(/_/g, ' ').replace(/\bai\b/i, 'AI');
const errorOf = (err: any, fallback: string): string => err?.response?.data?.error || fallback;

const proposalNote = (label: string, p?: Proposal): string | null => {
  if (!p) return null;
  if (p.error) return `${label}: could not be proposed.`;
  if (p.skipped) return `${label}: not proposed (${SKIP_REASON[p.skipped] || p.skipped.replace(/_/g, ' ')}).`;
  return `${label}: proposed. Review it in Approvals.`;
};

const ItemRow: React.FC<{
  item: Item;
  section: Section;
  busy: boolean;
  onSave: (id: string, text: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}> = ({ item, section, busy, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.text);
  useEffect(() => { setDraft(item.text); }, [item.text]);

  const meta: string[] = [];
  if (section === 'commitments') meta.push(item.owner === 'them' ? `Theirs${item.owner_name ? ` (${item.owner_name})` : ''}` : 'Ours');
  if (section === 'commitments' && item.due_date) meta.push(`by ${item.due_date}`);
  if (section === 'buying_signals' && item.type) meta.push(SIGNAL_LABEL[item.type] || item.type);
  if (section === 'buying_signals' && item.date) meta.push(item.date);
  if (section === 'decisions' && item.kind) meta.push(KIND_LABEL[item.kind] || item.kind);
  if (section === 'stakeholders') meta.push([item.title, item.org].filter(Boolean).join(', '));

  return (
    <li className="py-2" data-testid={`bd-debrief-item-${item.id}`}>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            className="w-full border border-gray-300 rounded p-2 text-sm"
            data-testid={`bd-debrief-edit-text-${item.id}`}
          />
          <div className="flex gap-2 mt-1">
            <button
              onClick={async () => { await onSave(item.id, draft); setEditing(false); }}
              disabled={busy || !draft.trim()}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
              data-testid={`bd-debrief-save-${item.id}`}
            >
              <Check className="w-3 h-3" /> Save
            </button>
            <button onClick={() => { setDraft(item.text); setEditing(false); }} className="flex items-center gap-1 text-xs px-2 py-1 rounded border text-gray-600">
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-gray-800">
              {item.text}
              {meta.filter(Boolean).length > 0 && <span className="text-xs text-gray-400"> · {meta.filter(Boolean).join(' · ')}</span>}
              {item.edited && <span className="ml-1 text-xs text-blue-600" title={item.original_text ? `Originally: ${item.original_text}` : undefined}>(edited)</span>}
              {item.added && <span className="ml-1 text-xs text-blue-600">(added by you)</span>}
              {item.restricted && (
                <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-amber-700" title="This passage was flagged as sensitive. It is kept out of drafts and CRM notes." data-testid="bd-debrief-restricted">
                  <ShieldAlert className="w-3 h-3" /> restricted
                </span>
              )}
            </div>
            {item.quote && (
              <div className="text-xs text-gray-500 mt-0.5" data-testid="bd-debrief-quote">
                <span className="italic">&ldquo;{item.quote}&rdquo;</span>
                {item.ref && <span className="text-gray-400"> — {item.ref}</span>}
              </div>
            )}
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={() => setEditing(true)} disabled={busy} className="p-1 text-gray-400 hover:text-gray-700" title="Edit" data-testid={`bd-debrief-edit-${item.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onDelete(item.id)} disabled={busy} className="p-1 text-gray-400 hover:text-red-600" title="Delete" data-testid={`bd-debrief-delete-${item.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

const AddRow: React.FC<{ section: Section; busy: boolean; onAdd: (section: Section, text: string) => Promise<void> }> = ({ section, busy, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1" data-testid={`bd-debrief-add-${section}`}>
        <Plus className="w-3 h-3" /> Add
      </button>
    );
  }
  return (
    <div className="mt-1">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="What should be on the debrief?"
        className="w-full border border-gray-300 rounded p-2 text-sm"
        data-testid={`bd-debrief-add-text-${section}`}
      />
      <div className="flex gap-2 mt-1">
        <button
          onClick={async () => { await onAdd(section, text); setText(''); setOpen(false); }}
          disabled={busy || !text.trim()}
          className="text-xs px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
          data-testid={`bd-debrief-add-save-${section}`}
        >
          Add
        </button>
        <button onClick={() => { setText(''); setOpen(false); }} className="text-xs px-2 py-1 rounded border text-gray-600">Cancel</button>
      </div>
    </div>
  );
};

const MeetingDebriefPanel: React.FC<{ meetingId: number; onChanged?: () => void }> = ({ meetingId, onChanged }) => {
  const [debrief, setDebrief] = useState<Debrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const base = `/api/bd/debriefs/meeting/${meetingId}`;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const told = useRef(false);
  // The latest callback, read when needed: a new function from the parent must never re-run the load.
  const changed = useRef(onChanged);
  changed.current = onChanged;

  const load = useCallback(async () => {
    try {
      const res = await api.get(base);
      setDebrief(res.data?.debrief || null);
      setFailed(false);
      // A debrief made automatically (right after something was logged) has already moved the stage on the server:
      // tell the page once, so the rail and the status chip are not left saying "waiting".
      if (res.data?.debrief?.status === 'ready' && !told.current) {
        told.current = true;
        if (changed.current) changed.current();
      }
    } catch (err) {
      setDebrief(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // A debrief being made on another request (the automatic one, right after something was logged): look again shortly.
  useEffect(() => {
    if (debrief && debrief.status === 'running') {
      timer.current = setTimeout(load, 4000);
    }
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [debrief, load]);

  const run = async (fn: () => Promise<any>, success?: string) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res?.data?.debrief) setDebrief(res.data.debrief);
      if (success) toast.success(success);
      if (changed.current) changed.current();
    } catch (err) {
      toast.error(errorOf(err, 'That did not work. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const generate = (force: boolean) => run(
    () => api.post(`${base}/generate`, { force }),
    force ? 'Re-run done. Your edits were kept.' : 'Debrief ready.'
  );
  const save = (id: string, text: string) => run(() => api.patch(`${base}/items/${id}`, { text }), 'Saved.');
  const remove = (id: string) => run(() => api.patch(`${base}/items/${id}`, { deleted: true }));
  const restore = (id: string) => run(() => api.patch(`${base}/items/${id}`, { deleted: false }));
  const add = (section: Section, text: string) => run(() => api.post(`${base}/items`, { section, text }), 'Added.');
  const tag = (t: string, present: boolean) => run(() => api.put(`${base}/tags/${t}`, { present }));

  if (loading) return <div className="flex items-center gap-2 text-sm text-gray-400 mb-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (failed || !debrief) return <p className="text-sm text-red-600 mb-6" data-testid="bd-debrief-error">The debrief could not be loaded.</p>;

  if (debrief.status === 'none') {
    const none = (debrief.captures || 0) === 0;
    return (
      <div className="mb-6 border border-dashed border-gray-200 rounded-lg p-4" data-testid="bd-debrief-none">
        <p className="text-sm font-medium text-slate-700">{none ? 'Nothing to debrief yet' : 'No debrief yet'}</p>
        <p className="text-xs text-gray-500 mt-1 mb-3">
          {none
            ? 'Log the call first at stage 2 (upload a transcript or paste notes). The debrief is made from it.'
            : 'The debrief is made automatically when something is logged. You can also make it now.'}
        </p>
        {!none && (
          <button onClick={() => generate(false)} disabled={busy} className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" data-testid="bd-debrief-generate">
            {busy ? 'Making it…' : 'Make the debrief'}
          </button>
        )}
      </div>
    );
  }

  if (debrief.status === 'running') {
    return <div className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-testid="bd-debrief-running"><Loader2 className="w-4 h-4 animate-spin" /> Making the debrief…</div>;
  }

  const counts = (k: Section) => (debrief[k] || []).length;
  const deleted = debrief.deleted_items || [];
  const zoho = proposalNote('Zoho update', debrief.proposals?.zoho);
  const followUp = proposalNote('Follow-up email', debrief.proposals?.follow_up);
  const activity = activityNote(debrief.proposals?.activity);
  // BD-09: what the Zoho update (and the follow-up) could not determine, e.g. the deal value.
  // BD-11: what was asked instead of added to Jarvis PM (a colleague's commitment, a personal item).
  const proposalQuestions = Array.from(new Set([
    ...(debrief.proposals?.zoho?.questions || []),
    ...(debrief.proposals?.follow_up?.questions || []),
    ...(debrief.proposals?.activity?.questions || []),
  ]));

  return (
    <div className="mb-6 space-y-5" data-testid="bd-debrief-panel">
      {debrief.status === 'failed' && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-3 text-sm text-red-700" data-testid="bd-debrief-failed">
          The last attempt did not finish. What you see below is the previous version.
          <button onClick={() => generate(true)} disabled={busy} className="ml-2 underline" data-testid="bd-debrief-retry">Try again</button>
        </div>
      )}

      {(debrief.sensitivity_flags || []).length > 0 && (
        <div className="flex items-start gap-1.5 border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-800" data-testid="bd-debrief-flagged">
          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            {(debrief.sensitivity_flags || []).length} passage{(debrief.sensitivity_flags || []).length === 1 ? ' was' : 's were'} flagged as sensitive (non-public information, personal details or a third party&apos;s confidential information).
            Anything from {(debrief.sensitivity_flags || []).length === 1 ? 'it' : 'them'} is kept out of drafts and CRM notes.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          {debrief.source ? SOURCE_LABEL[debrief.source] || debrief.source : ''}
          {debrief.source && debrief.source !== 'transcript' && ' — less to go on than a transcript, so treat it as lower confidence.'}
          {debrief.generated_at && <span className="text-gray-400"> · made {new Date(debrief.generated_at).toLocaleString()}</span>}
        </div>
        <button
          onClick={() => { if (window.confirm('Re-run the debrief? Your edits are kept.')) generate(true); }}
          disabled={busy}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded border text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          data-testid="bd-debrief-rerun"
        >
          <RotateCcw className="w-3 h-3" /> Re-run
        </button>
      </div>

      {SECTIONS.map((s) => (
        <div key={s.key} data-testid={`bd-debrief-section-${s.key}`}>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {s.label} <span className="text-gray-300 normal-case font-normal">({counts(s.key)})</span>
          </h4>
          {/* The hint explains what the items are; with none, the empty line below says what matters. */}
          {s.hint && counts(s.key) > 0 && <p className="text-xs text-gray-400 mt-0.5">{s.hint}</p>}
          {counts(s.key) === 0 ? (
            <p className="text-xs text-gray-400 mt-1">Nothing{s.key === 'buying_signals' ? ' was said about budget, timing, authority or process' : ''}.</p>
          ) : (
            <ul className="divide-y divide-gray-100 mt-1">
              {(debrief[s.key] || []).map((it) => (
                <ItemRow key={it.id} item={it} section={s.key} busy={busy} onSave={save} onDelete={remove} />
              ))}
            </ul>
          )}
          <AddRow section={s.key} busy={busy} onAdd={add} />
        </div>
      ))}

      <div data-testid="bd-debrief-tags">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topics</h4>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {TOPIC_TAGS.map((t) => {
            const on = (debrief.topic_tags || []).includes(t);
            return (
              <button
                key={t}
                onClick={() => tag(t, !on)}
                disabled={busy}
                className={`text-xs px-2 py-0.5 rounded-full border ${on ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}
                data-testid={`bd-debrief-tag-${t}`}
                aria-pressed={on}
              >
                {tagLabel(t)}
              </button>
            );
          })}
        </div>
      </div>

      {deleted.length > 0 && (
        <div>
          <button onClick={() => setShowDeleted((v) => !v)} className="text-xs text-gray-500 underline" data-testid="bd-debrief-show-deleted">
            {showDeleted ? 'Hide' : 'Show'} {deleted.length} deleted item{deleted.length === 1 ? '' : 's'}
          </button>
          {showDeleted && (
            <ul className="mt-1 space-y-1">
              {deleted.map((d) => (
                <li key={d.id} className="flex items-center justify-between text-xs text-gray-500">
                  <span className="line-through">{d.text}</span>
                  <button onClick={() => restore(d.id)} disabled={busy} className="text-blue-600 hover:underline" data-testid={`bd-debrief-restore-${d.id}`}>Restore</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {(zoho || followUp || activity) && (
        <div className="border-t border-gray-100 pt-3 text-xs text-gray-500 space-y-0.5" data-testid="bd-debrief-proposals">
          <div className="font-medium text-gray-600">What was proposed from this debrief</div>
          {zoho && <div>{zoho}</div>}
          {followUp && <div>{followUp}</div>}
          {activity && <div data-testid="bd-debrief-activity">{activity}</div>}
          {proposalQuestions.length > 0 && (
            <div className="pt-1" data-testid="bd-debrief-proposal-questions">
              <div className="font-medium text-gray-600">Questions for the Zoho update, follow-up and Jarvis PM (Jarvis never guesses these)</div>
              <ul className="list-disc pl-5 text-gray-700">
                {proposalQuestions.map((q) => <li key={q}>{q}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MeetingDebriefPanel;
